/**
 * Consent provider + acknowledgment gate (RQ78378 / RQ78379 / RQ10409305 /
 * RQ10773473).
 *
 * Mounted high in the tree (above AuthProvider). Until the user has a valid
 * current-version acknowledgment it renders a focus-trapping blocking modal
 * INSTEAD of its children — so AuthProvider/App never mount and NO first-party
 * cookie or cookie-like storage is written before acknowledgment (RQ10773473).
 * The blocking modal forces a choice before the site is usable (RQ10409305).
 *
 * After acknowledgment it drives the first-party product-analytics emit gate
 * (RQ78378/79, RFC-0974). There are no third-party trackers left — Sentry was
 * removed in RFC-1172, so no telemetry leaves the origin at all.
 * The logged-out→login account-link audit is handled by <ConsentAuthBridge/>.
 */
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { createNadDebug } from '../utils/debugLog'
import { postConsent } from '../api/consent'
import ConsentBackdrop from '../components/consent/ConsentBackdrop'
import ConsentBlockingModal from '../components/consent/ConsentBlockingModal'
import ConsentPreferencesModal from '../components/consent/ConsentPreferencesModal'
import { ConsentContext, type ConsentContextValue } from './consentContextRaw'
import {
  type ConsentCategories,
  type ConsentState,
  type SourceAction,
  CONSENT_JURISDICTION,
  CONSENT_POLICY_VERSION,
  defaultCategories,
} from './consentTypes'
import { isCategoryPermitted } from './consentResolution'
import { readConsent, readGpcSignal, writeConsent } from './consentStorage'
import { denyAnalytics, syncAnalyticsConsent } from './analyticsGate'

const log = createNadDebug('dw:cookie-consent')

export function ConsentProvider({ children }: { children: ReactNode }) {
  // Regime + policy version are fixed constants (strictest, all regions) — known
  // synchronously, so there is NO network fetch before acknowledgment.
  const jurisdiction = CONSENT_JURISDICTION
  const policyVersion = CONSENT_POLICY_VERSION

  const [gpc] = useState<boolean>(() => readGpcSignal())
  // Read-only at init — reading existing storage is allowed; we only WRITE on a
  // decision (the acknowledgment itself).
  const [storedConsent, setStoredConsent] = useState<ConsentState | null>(() => readConsent())
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)

  // "Effective consent": only a decision made at the CURRENT policy version
  // counts. A version bump invalidates the stored decision → `acknowledged` flips
  // false → the blocking modal returns and re-collects the choice. There is no
  // silent revert because the wall + the analytics deny/drain handle the stale case.
  const consent = useMemo<ConsentState | null>(() => {
    if (!storedConsent) return null
    return storedConsent.v === policyVersion ? storedConsent : null
  }, [storedConsent, policyVersion])

  // E2E / local-dev escape hatch: in a `vite` dev build (import.meta.env.DEV)
  // auto-acknowledge so Playwright specs and local dev are not gated by the
  // blocking wall. This is statically `false` and dead-code-eliminated in every
  // `vite build` artifact (DEV === false there), so it can NEVER affect a deployed
  // environment. A spec or developer that needs the real wall opts back in by
  // navigating with `?consentWall=1` (see cookie-consent-flow / remove-mixpanel
  // specs). NOTE: when bypassing, `consent` stays null → the analytics gate below
  // sees no analytics consent and stays denied, so no telemetry is emitted.
  const bypassWall =
    import.meta.env.DEV && !new URLSearchParams(window.location.search).has('consentWall')

  const acknowledged = consent !== null || bypassWall

  // Drive the product-analytics emit gate (RFC-0974). If acknowledgment is lost
  // (e.g. a policy-version bump invalidates a prior accept and the blocking modal
  // returns), deny + drain so events are not emitted without current consent.
  useEffect(() => {
    if (!acknowledged) {
      // No acknowledgment ⇒ no product-analytics emission (drains any queue).
      denyAnalytics()
      return
    }
    syncAnalyticsConsent(jurisdiction, consent?.cats, gpc)
  }, [acknowledged, jurisdiction, consent, gpc])

  const persist = useCallback(
    (cats: ConsentCategories, src: SourceAction) => {
      if (!policyVersion || !jurisdiction) return
      // writeConsent creates the device id + consent record — these are the
      // first writes, and they ARE the acknowledgment (exempt strictly-necessary
      // storage). Nothing is written before this point.
      const written = writeConsent({ policyVersion, cats, src })
      setStoredConsent(written)
      log({
        event: 'decision',
        src,
        jurisdiction,
        analytics: cats.analytics,
        functional: cats.functional,
        marketing: cats.marketing,
        gpc,
      })
      void postConsent({
        consent_event_id: written.eventId,
        device_id: written.did,
        categories: cats,
        jurisdiction,
        policy_version: policyVersion,
        source_action: src,
        gpc,
      }).catch((err) => {
        log({ event: 'server_record_failed', src, error: String(err) })
      })
    },
    [policyVersion, jurisdiction, gpc]
  )

  const acceptAll = useCallback(() => {
    // GPC is an absolute opt-out for analytics/marketing — clamp them off at
    // storage so they cannot be silently enabled later if GPC stops.
    const cats = gpc
      ? { analytics: false, functional: true, marketing: false }
      : { analytics: true, functional: true, marketing: true }
    persist(cats, 'accept_all')
  }, [persist, gpc])

  const rejectNonEssential = useCallback(() => {
    persist(defaultCategories(), 'reject_nonessential')
  }, [persist])

  const savePreferences = useCallback(
    (cats: ConsentCategories) => {
      persist(cats, 'save_preferences')
      setIsSettingsOpen(false)
    },
    [persist]
  )

  const openSettings = useCallback(() => setIsSettingsOpen(true), [])
  const closeSettings = useCallback(() => setIsSettingsOpen(false), [])

  const isCategoryAllowed = useCallback(
    (category: 'functional' | 'analytics' | 'marketing') =>
      isCategoryPermitted(category, jurisdiction, consent?.cats, gpc),
    [jurisdiction, consent, gpc]
  )

  const value: ConsentContextValue = useMemo(
    () => ({
      // Regime is a synchronous constant, so the provider is always "ready".
      isReady: true,
      jurisdiction,
      policyVersion,
      gpc,
      consent,
      acknowledged,
      isSettingsOpen,
      isCategoryAllowed,
      acceptAll,
      rejectNonEssential,
      savePreferences,
      openSettings,
      closeSettings,
    }),
    [
      jurisdiction,
      policyVersion,
      gpc,
      consent,
      acknowledged,
      isSettingsOpen,
      isCategoryAllowed,
      acceptAll,
      rejectNonEssential,
      savePreferences,
      openSettings,
      closeSettings,
    ]
  )

  // Gate: the app subtree mounts ONLY after acknowledgment, so no 1P cookie /
  // storage / network happens before the user chooses (critically, this also
  // prevents the GCP LB HTTP_COOKIE affinity cookie, which the LB would set on
  // any pre-consent API response). Regime is known synchronously, so there is no
  // loading state. Before acknowledgment we show a cookie-free decorative
  // backdrop with the blocking modal (translucent) on top. The preferences modal
  // is rendered here so it is reachable both pre-ack (from the blocking modal)
  // and post-ack (Header/Footer "Cookie settings").
  const body: ReactNode = acknowledged ? (
    children
  ) : (
    <>
      <ConsentBackdrop />
      <ConsentBlockingModal />
    </>
  )

  return (
    <ConsentContext.Provider value={value}>
      {body}
      <ConsentPreferencesModal isOpen={isSettingsOpen} onClose={closeSettings} />
    </ConsentContext.Provider>
  )
}
